import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPollByAny,
  selectAllPolls,
  selectFetchedPolls,
  selectPollsLoading,
  selectPollTotal,
  deletePollSurvey,
} from "@/store/redux/slices/polls_&_survey/polls_&_survey";
import { useLoader } from "@/hooks/LoaderProvider";
import { useNavigate } from "react-router-dom";
import { DownOutlined, PlusOutlined, Dropdown } from "@/config/antd";
import PageHeader from "@/components/layout/Header/Page_Header";
import Breadcrumb from "@/components/specific/Breadcrumb";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import PollCard from "@/pages/polls_&_survey/components/Polls_And_Survey_Card";
import EmptyState from "@/pages/polls_&_survey/components/Empty_State";
import PollsAndSurveysHomeTable from "@/pages/polls_&_survey/components/PollsAndSurveysHomeTable";
import TrashIcon from "@/assets/icons/web_Icons/trash_red.svg";
import { getOrganizationId } from "@/utils/token_functions";
import Success from "@/components/common/alerts/Success";
import Delete from "@/components/common/alerts/Delete";
import { selectUserProfile } from "@/store/redux/slices/user/user_slice";

const PollsAndSurvey = () => {
  const dispatch = useDispatch();
  const polls = useSelector(selectAllPolls);
  const fetchedPollsByOrg = useSelector(selectFetchedPolls);
  const isLoading = useSelector(selectPollsLoading);
  const totalPollsCount = useSelector(selectPollTotal);

  const [searchValue, setSearchValue] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [filterValue, setFilterValue] = useState("all"); // date filter: 'all' | 'today' | 'thisWeek' | 'thisMonth'
  const [selectedPolls, setSelectedPolls] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPolls, setTotalPolls] = useState(0);
  const pageSize = 10;
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  // Get the user profile
  const userProfile = useSelector(selectUserProfile);
  // State for success feedback modal
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [deletedPollName, setDeletedPollName] = useState("");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedPollToDelete, setSelectedPollToDelete] = useState(null);

  const getPollsToUse = useCallback(() => {
    const fetched = fetchedPollsByOrg ?? [];
    const allPolls = polls ?? [];
    return fetched.length > 0 ? fetched : allPolls;
  }, [fetchedPollsByOrg, polls]);

  // Update total polls count based on available sources
  useEffect(() => {
    const pollsCount = getPollsToUse().length;
    setTotalPolls(pollsCount > 0 ? pollsCount : totalPollsCount);
  }, [getPollsToUse, totalPollsCount]);

  const handleDelete = useCallback(async (pollId, pollTitle) => {
    // If called with a specific ID, it's an individual delete

    if (pollId) {
      setSelectedPollToDelete({ id: pollId, title: pollTitle });
      setIsDeleteModalVisible(true);
    }
  }, []);

  const handleBulkDelete = async () => {
    if (selectedPolls.length === 0) return;
    setIsDeleteModalVisible(false);

    showLoader();
    try {
      // Get the titles of the polls being deleted for the success message
      const pollsToDelete = getPollsToUse().filter(
        (poll) =>
          selectedPolls.includes(poll.id.toString()) ||
          selectedPolls.includes(poll._id)
      );

      const pollCount = pollsToDelete.length;

      // If only one poll, use its title, otherwise use count
      if (pollCount === 1) {
        setDeletedPollName(pollsToDelete[0].name);
      } else {
        setDeletedPollName(`${pollCount} polls`);
      }

      // Map the selected polls to their corresponding _id values for deletion
      const pollIdsToDelete = pollsToDelete.map((poll) => poll._id);

      // Delete all selected polls - making sure to use the correct ID format
      await Promise.all(
        pollIdsToDelete.map((pollId) =>
          dispatch(deletePollSurvey(pollId)).unwrap()
        )
      );

      // Refresh data and reset selection
      await handleRefresh();
      setSelectedPolls([]);
      setSuccessModalVisible(true);
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      hideLoader();
    }
  };

  // Add a function to handle confirmation from the Delete modal
  const handleDeleteConfirm = async () => {
    if (selectedPollToDelete) {
      setIsDeleteModalVisible(false);
      // Single poll delete
      showLoader();
      try {
        await dispatch(deletePollSurvey(selectedPollToDelete.id)).unwrap();
        setDeletedPollName(selectedPollToDelete.title);
        await handleRefresh();
        setIsDeleteModalVisible(false);
        setSuccessModalVisible(true);
        setSelectedPollToDelete(null);
      } catch (error) {
        console.error("Delete failed:", error);
      } finally {
        hideLoader();
      }
    } else {
      // Bulk delete
      await handleBulkDelete();
    }
  };

  const handleSuccessClose = () => {
    setSuccessModalVisible(false);
  };

  const handleModify = useCallback(
    (pollId) => {
      navigate(`/dashboard/polls/edit/${pollId}`);
    },
    [navigate]
  );

  const applyDateFilter = useCallback(
    (list) => {
      if (!filterValue || filterValue === "all") return list;

      const now = new Date();

      return list.filter((poll) => {
        const createdAt = poll.createdAt ? new Date(poll.createdAt) : null;
        if (!createdAt) return true;

        const sameDay =
          createdAt.getDate() === now.getDate() &&
          createdAt.getMonth() === now.getMonth() &&
          createdAt.getFullYear() === now.getFullYear();

        if (filterValue === "today") {
          return sameDay;
        }

        if (filterValue === "thisWeek") {
          const startOfWeek = new Date(now);
          startOfWeek.setHours(0, 0, 0, 0);
          startOfWeek.setDate(now.getDate() - now.getDay());

          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 7);

          return createdAt >= startOfWeek && createdAt < endOfWeek;
        }

        if (filterValue === "thisMonth") {
          return (
            createdAt.getMonth() === now.getMonth() &&
            createdAt.getFullYear() === now.getFullYear()
          );
        }

        return true;
      });
    },
    [filterValue]
  );

  // Transform data for cards (mobile view)
  const getFilteredCardsData = useCallback(() => {
    let filtered = getPollsToUse();

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter((poll) =>
        poll.name?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "published":
        filtered = filtered.filter((poll) => poll.status === "published");
        break;
      case "drafts":
        filtered = filtered.filter((poll) => poll.status === "draft");
        break;
      default:
        break;
    }

    // Apply date filter
    filtered = applyDateFilter(filtered);

    return filtered;
  }, [getPollsToUse, searchValue, activeFilter, applyDateFilter]);

  // Transform data for table (desktop view)
  const getFilteredTableData = useCallback(() => {
    let filtered = getPollsToUse();

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter((poll) =>
        poll.name?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "published":
        filtered = filtered.filter((poll) => poll.status === "published");
        break;
      case "drafts":
        filtered = filtered.filter((poll) => poll.status === "draft");
        break;
      default:
        break;
    }

    // Apply date filter
    filtered = applyDateFilter(filtered);

    return filtered;
  }, [getPollsToUse, searchValue, activeFilter, applyDateFilter]);

  useEffect(() => {
    const fetchPolls = async () => {
      const organizationID = getOrganizationId();

      showLoader();
      try {
        await dispatch(
          fetchPollByAny({
            key: "organizationID",
            value: organizationID,
          })
        ).unwrap();

        // Optionally keep the original fetch if needed for backward compatibility
        // await dispatch(fetchPollSurveysAsync({ skip: (currentPage - 1) * pageSize, limit: pageSize })).unwrap();
      } catch (error) {
        console.error("Error fetching polls:", error);
      } finally {
        hideLoader();
      }
    };

    fetchPolls();
  }, [dispatch, hideLoader, showLoader]);

  const handleRefresh = useCallback(async () => {
    // 1. Reset pagination to first page
    setCurrentPage(1);

    // 2. Clear the search term
    setSearchValue("");

    // 3. Reset the active filter to 'all'
    setActiveFilter("all");

    // 4. Reset the display text for the filter button
    setFilterValue("all");

    // 5. Fetch fresh data
    const organizationID = getOrganizationId();

    showLoader();
    try {
      await dispatch(
        fetchPollByAny({
          key: "organizationID",
          value: organizationID,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error refreshing polls:", error);
    } finally {
      hideLoader();
    }
  }, [dispatch, showLoader, hideLoader]);

  if (isLoading && getPollsToUse().length === 0) {
    return null;
  }

  const cardData = getFilteredCardsData();
  const tableData = getFilteredTableData();

  // Calculate counts for filter buttons
  const publishedCount =
    getPollsToUse().filter((p) => p.status === "published").length || 0;
  const draftsCount =
    getPollsToUse().filter((p) => p.status === "draft").length || 0;
  const allCount = getPollsToUse().length || 0;

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleFilterChange = (infoOrKey) => {
    const key =
      typeof infoOrKey === "string" ? infoOrKey : infoOrKey?.key ?? "all";
    setFilterValue(key);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAddNew = () => {
    if (selectedPolls.length > 0) {
      // Show delete confirmation dialog
      setIsDeleteModalVisible(true);
    } else {
      // Navigate to add new poll page
      navigate("/dashboard/polls/add");
    }
  };

  const filterOptions = {
    items: [
      { key: "all", label: "All Time" },
      { key: "today", label: "Today" },
      { key: "thisWeek", label: "This Week" },
      { key: "thisMonth", label: "This Month" },
    ],
    onClick: handleFilterChange,
    className: "custom-dropdown-menu",
  };

  const moreOptionsMenu = {
    items: [
      { key: "1", label: "Export" },
      { key: "2", label: "Print" },
    ],
  };

  // breadcrumb items
  const items = [{ title: "Polls and Surveys", link: "/dashboard/polls" }];

  // Define sort options based on your filter buttons
  const sortOptions = [
    { key: "all", label: "All Surveys" },
    { key: "published", label: "Published" },
    { key: "drafts", label: "Drafts" },
  ];

  return (
    <div className="w-full">
      <Breadcrumb items={items} />
      <PageHeader title="Polls and Surveys" />

      <div className="flex items-center justify-between my-5">
        <div className="flex items-center gap-5">
          <div className="flex flex-col text-left">
            <h1
              className="heading-medium text-lighterGrey"
              data-testid="polls-count">
              {totalPolls} Total Surveys
            </h1>
            <h1 className="text-regular text-lighterGrey">
              Total number of Survey created
            </h1>
          </div>
          <div className="items-center hidden gap-2 lg:flex">
            <Button
              text={`All Surveys (${allCount})`}
              color="primary"
              className="px-2 py-1 text-sm sm:px-3 sm:py-2 sm:text-small min-w-[80px] sm:min-w-[100px]"
              iconPosition="left"
              customBgColor={activeFilter === "all" ? "primary" : "white"}
              customTextColor={activeFilter === "all" ? "white" : "primary"}
              onClick={() => setActiveFilter("all")}
            />
            <Button
              text={`Published (${publishedCount})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "published" ? "primary" : "white"}
              customTextColor={
                activeFilter === "published" ? "white" : "primary"
              }
              onClick={() => setActiveFilter("published")}
            />
            <Button
              text={`Drafts (${draftsCount})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "drafts" ? "primary" : "white"}
              customTextColor={activeFilter === "drafts" ? "white" : "primary"}
              onClick={() => setActiveFilter("drafts")}
            />
          </div>
        </div>
        <div className="items-center hidden gap-3 lg:flex">
          <Button
            text={selectedPolls.length !== 0 ? "DELETE SELECTED" : "ADD NEW"}
            icon={
              selectedPolls.length !== 0 ? (
                <img src={TrashIcon} />
              ) : (
                <PlusOutlined />
              )
            }
            color={selectedPolls.length !== 0 ? "outline" : "primary"}
            className="border border-primary font-normal text-[11px] !px-3 !py-2 h-8 whitespace-nowrap"
            iconPosition="left"
            customBgColor={selectedPolls.length !== 0 ? "" : "white"}
            customTextColor={selectedPolls.length !== 0 ? "red" : "primary"}
            onClick={handleAddNew}
          />
          <Dropdown menu={filterOptions} trigger={["click"]}>
            <Button
              text={
                filterOptions.items.find((opt) => opt.key === filterValue)
                  ?.label || "FILTER BY"
              }
              icon={<DownOutlined />}
              iconPosition="right"
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor="white"
              customTextColor="black"
            />
          </Dropdown>
        </div>
        <Button
          text="ADD NEW"
          icon={<PlusOutlined />}
          color="primary"
          className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] whitespace-nowrap sm:min-w-[100px] lg:hidden"
          iconPosition="left"
          customBgColor="primary"
          customTextColor="white"
          onClick={handleAddNew}
        />
      </div>

      <SearchFilterBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onFilterChange={(value) => {
          setActiveFilter(value);
        }}
        filterValue={filterValue}
        filterOptions={filterOptions}
        moreOptionsMenu={moreOptionsMenu}
        placeholder="Search..."
        filterType="dropdown"
        sortOptions={sortOptions}
        searchValue={searchValue}
      />

      {/* Conditional Rendering based on data and filters */}
      {!getPollsToUse()?.length ? (
        <EmptyState onSetup={handleAddNew} />
      ) : cardData.length === 0 && tableData.length === 0 ? (
        <EmptyState onSetup={handleAddNew} />
      ) : (
        <>
          {/* Desktop table view */}
          <div className="hidden lg:block">
            <PollsAndSurveysHomeTable
              data-testid="polls-table"
              setSelectedPolls={setSelectedPolls}
              polls={tableData}
              loading={isLoading}
              onDelete={handleDelete}
              onModify={handleModify}
            />
          </div>

          {/* Mobile card view */}
          <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2 lg:hidden">
            {cardData.map((poll) => (
              <PollCard
                key={poll?._id}
                id={poll?.id}
                _id={poll?._id}
                title={poll?.name}
                description={poll?.description}
                schedule={poll?.schedule}
                createdAt={poll?.createdAt}
                author={userProfile?.fullname}
                avatar={poll?.profileImage}
                onDelete={handleDelete}
                onModify={handleModify}
              />
            ))}
          </div>

          <div className="mt-6">
            <CustomPagination
              current={currentPage}
              total={totalPolls}
              pageSize={pageSize}
              onChange={handlePageChange}
              showingText={`Showing ${cardData.length} of ${totalPolls} records`}
              className="pb-8"
            />
          </div>
        </>
      )}

      {/* Success Modal */}
      <Success
        isVisible={successModalVisible}
        onClose={handleSuccessClose}
        header="Poll Deleted"
        paragraph={`"${deletedPollName}" has been successfully deleted.`}
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={handleSuccessClose}
      />

      {/* Delete confirmation modal */}
      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Poll?"
        paragraph={
          selectedPolls.length > 0
            ? `Are you sure you want to delete ${selectedPolls.length} selected polls? This action cannot be undone.`
            : `Are you sure you want to delete "${selectedPollToDelete?.title}"? This action cannot be undone.`
        }
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export default PollsAndSurvey;
